import React, { useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { Button } from 'primereact/button'

import { AlertIcon } from '../lib/icons/alert-icon'
import { isAdminDomain, isPartnerDomain, isSupportDomain, isUserappDomain } from '../lib/currentDomain'
import useText from '../common/hooks/useText'
import isAura from '../lib/isAura'
import { theme } from '../lib/theme'
import isHalo from '../lib/isHalo'
import isDawn from '../lib/isDawn'
import isLuna from '../lib/isLuna'
import { ChargePoint, SmartChargingSchedule, SyncStatuses } from '../common/redux/api/web-api'
import { Dropdown } from '../common/uikit/Dropdown'
import timeZones from '../lib/timeZones'
import Toggle from '../common/components/Toggle'

import Text from './Text'
import ConnectorSelect, { ConnectorType, IConnectorId } from './ConnectorSelect'
import CurrentSelect from './CurrentSelect'
import Modal from './Modal'
import EditChargePointModal from './EditChargePointModal'

const rotationKeyframes = keyframes`
    100% {
        transform:rotate(360deg);
    }
`

const SyncStatusIcon = styled.img<{ rotating?: boolean }>`
    ${(p) =>
        p.rotating &&
        css`
            animation: ${rotationKeyframes} 4s linear infinite;
        `}
    width: 2em;
    margin-right: 0.5rem;
`

const fadeOutKeyframes = keyframes`
    0% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
`

const FadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    animation: ${fadeOutKeyframes} 2s forwards ease 5s;
`

const SyncedStatus = ({ syncStatus }: { syncStatus: SyncStatuses }) => {
    if (syncStatus === 'Synced') {
        return (
            <FadingWrapper>
                <SyncStatusIcon src={'/common/content/images/synced.png'} />
                Synced
            </FadingWrapper>
        )
    }

    return null
}

const SyncStatus = ({ syncStatus }: { syncStatus: SyncStatuses }) => {
    if (syncStatus === 'Syncing') {
        return (
            <span title="It take times to apply changes to charger. Please make sure your charger is online if charger is not charging according to latest schedule.">
                <SyncStatusIcon src={'/common/content/images/syncing.png'} rotating />
                Syncing
            </span>
        )
    }

    if (syncStatus === 'NotSynced') {
        return (
            <span title="It take times to apply changes to charger. Please make sure your charger is online if charger is not charging according to latest schedule.">
                <SyncStatusIcon src={'/common/content/images/not-synced.png'} />
                Not synced
            </span>
        )
    }

    return null
}

export default function ChargePointSchedulesControls({
    chargePoint,
    schedule,
    onUpdateSchedule,
    numberOfConnectors,
    connectorId,
    onChangeConnectorId,
    onClearSchedule,
    minPossibleCurrent,
    maxPossibleCurrent,
    onSaveSchedule,
    disabledSave,
    disabledDefaultCurrent,
    disabledControls,
    disabledClear,
    syncStatus = 'Synced',
    showSyncedStatusIcon,
}: Props) {
    const text = useText()

    const [visibleChargePointEditor, setChargePointEditorVisibility] = useState(false)

    if (!chargePoint) return null

    const isSchukodisabledforAdmin = isHalo(chargePoint) && connectorId === ConnectorType.CHARGER && isAdminDomain
    const isDefaultCurrentEnabled =
        isSupportDomain || isPartnerDomain || isAura(chargePoint) || isDawn(chargePoint) || isLuna(chargePoint)

    return (
        <div className="ml-6">
            <div>
                {!isAura(chargePoint) ? (
                    <div>
                        {connectorId === 1 ? (
                            <div>
                                <Text
                                    code="msgSmartScheduleChargerUseInstruction"
                                    values={{
                                        maximumCurrent: maxPossibleCurrent,
                                        editCurrent: (
                                            <Button
                                                onClick={() => setChargePointEditorVisibility(true)}
                                                data-testid="edit-button"
                                                className="pointer text-blue-500 p-0 w-0"
                                                icon="pi pi-pencil"
                                                title={text('msgEditMaxCurrent')}
                                                outlined
                                                text
                                                disabled={disabledControls}
                                            ></Button>
                                        ),
                                    }}
                                />
                            </div>
                        ) : (
                            <Text code="msgSmartScheduleSocketUseInstruction" />
                        )}
                    </div>
                ) : (
                    <div>
                        <Text
                            code="msgSmartScheduleChargerUseInstruction"
                            values={{
                                maximumCurrent: maxPossibleCurrent,
                                editCurrent: (
                                    <Button
                                        onClick={() => setChargePointEditorVisibility(true)}
                                        data-testid="edit-button"
                                        className="pointer text-blue-500 p-0 w-0"
                                        icon="pi pi-pencil"
                                        title={text('msgEditMaxCurrent')}
                                        outlined
                                        text
                                        disabled={disabledControls}
                                    ></Button>
                                ),
                            }}
                        />
                    </div>
                )}

                {isUserappDomain && typeof schedule.defaultCurrent !== 'undefined' && schedule.defaultCurrent > 0 ? (
                    <span className="notification-class ">
                        <span className="alert-icon">
                            <AlertIcon size={28} fill={theme.brandColors.CA_Black} />
                        </span>
                        <span className="mt-2">
                            <Text
                                code="msgUserSmartSchedulingNotifications"
                                values={{ default: Math.round(schedule.defaultCurrent) }}
                            ></Text>
                        </span>
                    </span>
                ) : null}
                <div className="grid mt-3">
                    <div className="flex col-12 flex-column lg:flex-row justify-content-between gap-3">
                        {numberOfConnectors > 1 && (
                            <ConnectorSelect
                                connectors={chargePoint.connectors ?? []}
                                value={connectorId}
                                onChange={onChangeConnectorId}
                            />
                        )}

                        <div className="flex gap-2">
                            <Button
                                disabled={disabledClear}
                                onClick={() => {
                                    if (window.confirm(text('msgClearScheduleConfirm'))) onClearSchedule()
                                }}
                            >
                                <Text code="btnClearSchedule" />
                            </Button>
                            <Button
                                onClick={onSaveSchedule}
                                disabled={!!disabledSave || disabledControls}
                                title={typeof disabledSave === 'string' ? disabledSave : undefined}
                            >
                                <Text code="btnSaveSchedule" />
                            </Button>
                        </div>
                    </div>

                    {isSupportDomain ? (
                        <div className="col-12 flex align-items-center gap-2 mt-3">
                            {text('lblScheduling')}:{' '}
                            <Toggle
                                disabled={disabledControls}
                                showStateLabel
                                isToggled={!!schedule.isActive}
                                toggleFn={() => onUpdateSchedule({ ...schedule, isActive: !schedule.isActive })}
                            />
                        </div>
                    ) : null}

                    {(isSchukodisabledforAdmin || isDefaultCurrentEnabled) && !isUserappDomain && (
                        <div className="col-12 md:col-6">
                            <CurrentSelect
                                label={text('msgSetFollowingCurrentForUnscheduledSlots')}
                                infoText={!isUserappDomain && text('msgSmartScheduleChargerUseInstructionshort')}
                                value={schedule.defaultCurrent}
                                onChange={(event) => onUpdateSchedule({ ...schedule, defaultCurrent: event.value })}
                                minValue={minPossibleCurrent}
                                maxValue={maxPossibleCurrent}
                                placeholder={text('lblSelectOption')}
                                disabled={disabledControls || disabledDefaultCurrent}
                                isSocket={!isAura(chargePoint) && connectorId === ConnectorType.SOCKET}
                                error={(() => {
                                    if (schedule.defaultCurrent && schedule.defaultCurrent > maxPossibleCurrent) {
                                        return connectorId === ConnectorType.CHARGER || isAura(chargePoint)
                                            ? text('msgMaximumCurrentReached', { maxPossibleCurrent })
                                            : `Maximum current is set to ${maxPossibleCurrent} A. Socket might not be charging optimally. To ensure optimal charging for socket, set current to 10 A and above.`
                                    }
                                })()}
                            />
                        </div>
                    )}

                    <div className="col-12 md:col-6">
                        <Dropdown
                            label={text('lblSelectTimeZone')}
                            infoText={text('msgTimeZoneShouldHaveSameLocationAsCharger')}
                            disabled={disabledControls}
                            value={schedule.timeZone}
                            onChange={(event) => {
                                onUpdateSchedule({ ...schedule, timeZone: event.value })
                            }}
                            options={timeZones.map((tz) => ({
                                label: `(GMT${tz.utc}) ${tz.name}`,
                                value: tz.code,
                            }))}
                            error={schedule.timeZone ? undefined : text('errTimeZoneRequired')}
                            id="time-zone-select"
                        />
                    </div>
                </div>
            </div>
            <SyncStatus syncStatus={syncStatus} />
            {showSyncedStatusIcon && <SyncedStatus syncStatus={syncStatus} />}

            {visibleChargePointEditor && chargePoint && (
                <Modal>
                    <EditChargePointModal
                        chargePoint={chargePoint}
                        close={() => {
                            setChargePointEditorVisibility(false)
                        }}
                        dismiss={() => setChargePointEditorVisibility(false)}
                    />
                </Modal>
            )}
        </div>
    )
}

export type Props = {
    chargePoint?: ChargePoint
    schedule: SmartChargingSchedule
    onUpdateSchedule: (schedule: SmartChargingSchedule) => void
    numberOfConnectors: number
    connectorId: IConnectorId
    onChangeConnectorId: (connectorId: IConnectorId) => void
    onClearSchedule: () => void
    minPossibleCurrent: number
    maxPossibleCurrent: number
    visibleTimeZoneSelector?: boolean
    onSaveSchedule: (event: React.MouseEvent<HTMLButtonElement>) => void
    disabledSave?: boolean | string
    disabledDefaultCurrent?: boolean
    disabledControls?: boolean
    disabledClear: boolean
    syncStatus?: SyncStatuses
    showSyncedStatusIcon: boolean
}
